import { t } from '@lingui/macro';
import { getToken } from '@luminovo/auth';
import { assertUnreachable, formatInteger } from '@luminovo/commons';
import { Link, PropertiesTable } from '@luminovo/design-system';
import { CustomFullPart, http, PartLite, PartLiteTypes } from '@luminovo/http-client';
import { formatAttachment, formatComplianceStatus, formatPackage } from '@luminovo/sourcing-core';
import { useQuery } from '@tanstack/react-query';

export function PartView({ part }: { part: PartLite }): JSX.Element {
    if (part.kind === PartLiteTypes.Custom) {
        return <PartViewCustom part={part} />;
    }
    if (part.kind === PartLiteTypes.CustomComponent) {
        return <PartViewCustomComponent part={part} />;
    }
    if (part.kind === PartLiteTypes.Ipn) {
        return <PartViewIpn part={part} />;
    }
    if (part.kind === PartLiteTypes.OffTheShelf) {
        return <PartViewOffTheShelf part={part} />;
    }
    if (part.kind === PartLiteTypes.Generic) {
        return <PartViewGeneric part={part} />;
    }
    if (part.kind === PartLiteTypes.Unknown || part.kind === PartLiteTypes.RawSpecification) {
        return <PartViewUnknown part={part} />;
    }

    assertUnreachable(part);
}

function PartViewCustom({ part }: { part: PartLite & { kind: PartLiteTypes.Custom } }) {
    const { data: customPart } = useQuery({
        queryKey: ['custom-part', part.id],
        queryFn: () => http('GET /parts/custom/:partId', { pathParams: { partId: part.id } }, getToken()),
        select: (data): CustomFullPart => data.data,
    });

    const { data: attachments } = useQuery({
        queryKey: ['custom-part-attachments', part.id],
        queryFn: () => http('GET /parts/custom/:partId/resources', { pathParams: { partId: part.id } }, getToken()),
        select: (data): string[] => data.items,
    });

    const iterateAttachments: Record<string, JSX.Element> = {};
    let i = 0;
    for (const attachment of attachments ?? []) {
        i++;
        const fileName = formatAttachment(attachment);
        iterateAttachments[t`File ${i}`] = (
            <Link href={attachment} target="_blank">
                {fileName}
            </Link>
        );
    }

    return (
        <>
            <PropertiesTable
                variant="side-by-side"
                properties={{
                    [t`Part type`]: t`Custom part`,
                    [t`Category`]: part.type.name,
                    [t`Description`]: part.description ?? '-',
                    [t`Reach compliant`]: formatComplianceStatus(customPart?.reach_compliant),
                    [t`RoHS compliant`]: formatComplianceStatus(customPart?.rohs_compliant),
                    [t`Package`]: formatPackage(customPart?.package),
                    [t`Total files`]: <>{formatInteger(attachments?.length ?? 0)}</>,
                    ...iterateAttachments,
                }}
            />
        </>
    );
}

function PartViewCustomComponent({ part }: { part: PartLite & { kind: PartLiteTypes.CustomComponent } }) {
    return (
        <PropertiesTable
            variant="side-by-side"
            properties={{
                [t`Part type`]: t`Custom component`,
                [t`Linked parts`]: formatInteger(part.parts.length),
                [t`Description`]: part.description ?? '-',
            }}
        />
    );
}

function PartViewIpn({ part }: { part: PartLite & { kind: PartLiteTypes.Ipn } }) {
    return (
        <PropertiesTable
            variant="side-by-side"
            properties={{
                [t`Part type`]: t`Component`,
                [t`IPN`]: part.id,
                [t`Matches`]: formatInteger(part.matches.length),
            }}
        />
    );
}

function PartViewOffTheShelf({ part }: { part: PartLite & { kind: PartLiteTypes.OffTheShelf } }) {
    return (
        <PropertiesTable
            variant="side-by-side"
            properties={{
                [t`Part type`]: t`Off the shelf`,
                [t`Description`]: t`Exact MPN match found`,
                [t`Manufacturer`]: part.manufacturer.name,
                [t`MPN`]: part.mpn,
            }}
        />
    );
}

function PartViewGeneric({ part }: { part: PartLite & { kind: PartLiteTypes.Generic } }) {
    return (
        <PropertiesTable
            variant="side-by-side"
            properties={{
                [t`Part type`]: t`Generic`,
                [t`Matches`]: formatInteger(part.matches.length),
                [t`Description`]: part.content.type,
            }}
        />
    );
}

function PartViewUnknown({
    part,
}: {
    part: PartLite & { kind: PartLiteTypes.Unknown | PartLiteTypes.RawSpecification };
}) {
    return (
        <PropertiesTable
            variant="side-by-side"
            properties={{
                [t`Part type`]: t`Unknown`,
                [t`Description`]: t`No exact match was found for this part`,
                [t`MPN`]: part.mpn ?? '-',
                [t`Manufacturer`]: part.manufacturer.name ?? '-',
            }}
        />
    );
}
